import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { openRazorpayCheckout } from "@/lib/payments";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, ShieldCheck, Lock, Sparkles, Wallet } from "lucide-react";

// Plan → tier metadata shown on the card. Keys align with backend MEMBERSHIP_PLANS.
const CARD_META = {
  pnb: {
    eyebrow: "PAY NOTHING BEFORE",
    headline: "Free · 70% of every booking",
    perks: [
      "No upfront fee — activate instantly",
      "Keep 70% of every paid session",
      "Never expires · switch to EEA anytime",
      "Auto-payout via Razorpay Route (after KYC)",
    ],
    ctaBusy: "Activating…",
    ctaIdle: "Choose Pay Nothing Before",
  },
  monthly: {
    eyebrow: "EARN EVERYTHING AFTER",
    headline: "₹1,000 / month · 100% of every booking",
    perks: [
      "Full payout — you keep 100% of every session",
      "Renews every 30 days · pause anytime",
      "Priority listing on the booking page",
      "Direct settlements to your bank account",
    ],
    ctaBusy: "Opening secure payment…",
    ctaIdle: "Choose Earn Everything After",
  },
};

export default function MembershipCheckout() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    api.get("/counselor-membership/plans")
      .then(r => setPlans(r.data))
      .catch(() => toast.error("Could not load membership options."));
  }, []);

  const membershipStatus = user?.membership_status;
  const currentTier = user?.membership_tier;
  const kycStatus = user?.kyc_status || "not_submitted";
  const expiry = user?.membership_expires_at
    ? new Date(user.membership_expires_at).toLocaleDateString()
    : null;

  const heading = useMemo(() => {
    if (membershipStatus === "expired") return "Renew or switch your tier.";
    if (currentTier === "eea" && membershipStatus === "active") return "You're on Earn Everything After.";
    if (currentTier === "pnb" && membershipStatus === "active") return "You're on Pay Nothing Before.";
    return "Choose how you want to get paid.";
  }, [membershipStatus, currentTier]);

  const subline = useMemo(() => {
    if (membershipStatus === "approved_awaiting_payment")
      return "Your application is approved. Pick a tier — both start you as a bookable counsellor right away.";
    if (currentTier === "pnb" && membershipStatus === "active")
      return "You keep 70% of every booking. Upgrade to Earn Everything After any time for 100%.";
    if (currentTier === "eea" && membershipStatus === "active" && expiry)
      return `Active until ${expiry}. You keep 100% of every booking. Extend anytime — remaining time is preserved.`;
    if (membershipStatus === "expired")
      return `Your paid membership ended on ${expiry}. Renew EEA or switch to the free Pay Nothing Before tier.`;
    return "Both tiers activate your counsellor dashboard immediately. Payouts require a one-time KYC & bank verification.";
  }, [membershipStatus, currentTier, expiry]);

  const activatePnb = async () => {
    if (busyKey) return;
    setBusyKey("pnb");
    try {
      await api.post("/counselor-membership/activate-pnb");
      toast.success("Pay Nothing Before activated — you're bookable now.");
      if (typeof refresh === "function") await refresh();
      // Route straight into KYC & payout setup so payouts can flow.
      nav("/counselor/kyc?next=/counselor");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const payEea = async (planKey) => {
    if (busyKey) return;
    setBusyKey(planKey);
    try {
      const price = plans[planKey].price;
      const result = await openRazorpayCheckout({
        paymentType: "membership",
        plan: planKey,
        description: `${plans[planKey].label} — ₹${Math.round(price)}`,
        prefill: { name: user?.name, email: user?.email },
      });
      if (result.status === "success") {
        toast.success("Membership active — welcome to the team");
        if (typeof refresh === "function") await refresh();
        nav("/counselor/kyc?next=/counselor");
        return;
      }
      if (result.status === "failed") toast.error(result.error || "Payment failed — please try again");
      if (result.status === "dismissed") toast.message("Payment cancelled. You can retry anytime.");
    } catch (e) {
      toast.error(e.message || "Something went wrong.");
    } finally {
      setBusyKey(null);
    }
  };

  if (!plans) return <div className="p-16 text-center" data-testid="membership-loading">Loading…</div>;

  const orderedKeys = ["pnb", "monthly"].filter((k) => plans[k]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-14" data-testid="membership-page">
      <span className="label-eyebrow">Counsellor membership</span>
      <h1 className="font-serif-display text-4xl sm:text-5xl mt-2 mb-3" data-testid="membership-heading">{heading}</h1>
      <p className="mb-10 text-sm max-w-2xl" style={{ color: "var(--text-secondary)" }} data-testid="membership-subline">
        {subline}
      </p>

      <div className="grid md:grid-cols-2 gap-6 mb-10" data-testid="membership-plans">
        {orderedKeys.map((key) => {
          const p = plans[key];
          const meta = CARD_META[key];
          const isCurrent = currentTier === (p.tier || key) && membershipStatus === "active" &&
            (key === "pnb" || user?.membership_plan === key);
          return (
            <div
              key={key}
              data-testid={`plan-card-${key}`}
              className="rounded-2xl p-7 flex flex-col justify-between transition-all"
              style={{
                border: isCurrent ? "2px solid var(--brand)" : "1px solid var(--border-soft)",
                background: isCurrent ? "rgba(91,117,83,0.05)" : "#fff",
                minHeight: 380,
              }}
            >
              <div>
                <div className="flex justify-between items-start">
                  <span className="label-eyebrow" style={{ color: "var(--brand)" }}>{meta.eyebrow}</span>
                  {isCurrent && (
                    <span className="chip" style={{ background: "var(--brand)", color: "#fff", fontSize: "0.65rem" }}>
                      Current tier
                    </span>
                  )}
                </div>
                <div className="font-serif-display text-3xl mt-3">{p.label}</div>
                <div className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>{p.tagline}</div>
                <div className="mt-5 font-serif-display" style={{ fontSize: "2.4rem", lineHeight: 1 }}>
                  {p.price > 0 ? `₹${Math.round(p.price).toLocaleString("en-IN")}` : "Free"}
                  <span className="text-sm ml-2" style={{ color: "var(--text-secondary)" }}>
                    {p.billing === "monthly" ? "/month" : p.billing === "yearly" ? "/year" : "· forever"}
                  </span>
                </div>
                <ul className="mt-5 space-y-2 text-sm" data-testid={`plan-perks-${key}`}>
                  {meta.perks.map((perk) => (
                    <li key={perk} className="flex gap-2 items-start">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--brand)" }} />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-6">
                {key === "pnb" ? (
                  <button
                    data-testid="activate-pnb"
                    disabled={!!busyKey || (isCurrent && kycStatus !== "not_submitted")}
                    onClick={activatePnb}
                    className="btn-primary w-full"
                  >
                    {isCurrent
                      ? "Already active — continue to payout setup"
                      : busyKey === "pnb"
                        ? meta.ctaBusy
                        : meta.ctaIdle}
                  </button>
                ) : (
                  <button
                    data-testid="pay-eea-monthly"
                    disabled={!!busyKey}
                    onClick={() => payEea(key)}
                    className="btn-primary w-full"
                  >
                    {busyKey === key ? meta.ctaBusy : meta.ctaIdle}
                  </button>
                )}
                {isCurrent && key === "pnb" && kycStatus !== "not_submitted" && (
                  <button
                    data-testid="goto-kyc"
                    onClick={() => nav("/counselor/kyc")}
                    className="btn-secondary w-full mt-3"
                  >
                    Review KYC status
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="card-soft p-5 mb-4 flex items-center gap-4"
        style={{ border: "1px solid var(--border-soft)", background: "rgba(91,117,83,0.04)" }}>
        <Wallet className="w-10 h-10 flex-shrink-0" style={{ color: "var(--brand)" }} />
        <div>
          <div className="font-serif-display text-lg">Payouts via Razorpay Route</div>
          <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
            Both tiers settle directly into your verified bank account. After picking a tier we'll
            collect your KYC (PAN, address, bank details) and Counseltaria's admin will verify it
            before payouts turn on.
          </div>
        </div>
      </div>

      <div className="card-soft p-5 flex items-center gap-4"
        style={{ border: "1px solid var(--border-soft)", background: "rgba(91,117,83,0.04)" }}>
        <ShieldCheck className="w-10 h-10 flex-shrink-0" style={{ color: "var(--brand)" }} />
        <div>
          <div className="font-serif-display text-lg">Secure Payment</div>
          <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
            <Lock className="inline w-3 h-3 mr-1" style={{ color: "var(--brand)" }} />
            UPI · Cards · Net Banking · Wallets — powered by Razorpay
          </div>
        </div>
      </div>
    </div>
  );
}
